// src/pages/PageMenu/AccountsMenu.jsx
// قائمة المالية والحسابات — بالتصميم الموحّد (MenuKit) مع دعم الترجمة

import React from "react";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";
import { MenuPage } from "../../components/Menu/MenuKit";

import AccountTreeIcon from "@mui/icons-material/AccountTree";
import SettingsIcon from "@mui/icons-material/Settings";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PaymentIcon from "@mui/icons-material/Payment";
import PostAddIcon from "@mui/icons-material/PostAdd";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import ArrowCircleDownOutlinedIcon from "@mui/icons-material/ArrowCircleDownOutlined";
import ArrowCircleUpOutlinedIcon from "@mui/icons-material/ArrowCircleUpOutlined";
import CalculateIcon from "@mui/icons-material/Calculate";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import BalanceIcon from "@mui/icons-material/Balance";
import InventoryOutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import TodayIcon from "@mui/icons-material/Today";
import PercentIcon from "@mui/icons-material/Percent";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";

export default function AccountsMenu() {
  const { hasPermission } = useAuth();
  const can = (key) => !!PERMISSIONS[key] && hasPermission(PERMISSIONS[key]);
  const canProfit = PERMISSIONS.REPORT_PROFIT ? hasPermission(PERMISSIONS.REPORT_PROFIT) : can("REPORT_VIEW");

  const sections = [
    {
      key: "definitions",
      titleKey: "accountsMenu.sections.definitions", titleFallback: "التعاريف",
      subtitleKey: "accountsMenu.sections.definitionsDesc", subtitleFallback: "دليل الحسابات والصناديق والبنوك",
      icon: <CategoryOutlinedIcon />, color: "#6366F1",
      items: [
        can("CHART_OF_ACCOUNTS_VIEW") && { key: "coa", nameKey: "accountsMenu.chartOfAccounts", nameFallback: "دليل الحسابات", descFallback: "شجرة الحسابات وأرقامها", icon: <AccountTreeIcon />, color: "#6366F1", link: "/chart-of-accounts" },
        can("GL_SETTINGS_VIEW") && { key: "gl", nameKey: "accountsMenu.glSettings", nameFallback: "توجيه الحسابات", descFallback: "ربط العمليات بالحسابات", icon: <SettingsIcon />, color: "#475569", link: "/gl-settings" },
        can("SAFE_VIEW") && { key: "safes", nameKey: "accountsMenu.safes", nameFallback: "الصناديق", icon: <AccountBalanceWalletIcon />, color: "#B45309", link: "/safes" },
        can("BANK_VIEW") && { key: "banks", nameKey: "accountsMenu.banks", nameFallback: "البنوك", icon: <AccountBalanceIcon />, color: "#2563EB", link: "/banks" },
        can("PAYMENT_METHOD_VIEW") && { key: "pm", nameKey: "accountsMenu.paymentMethods", nameFallback: "طرق الدفع", icon: <PaymentIcon />, color: "#DB2777", link: "/payment-methods" },
      ],
    },
    {
      key: "operations",
      titleKey: "accountsMenu.sections.operations", titleFallback: "العمليات",
      subtitleKey: "accountsMenu.sections.operationsDesc", subtitleFallback: "القيود والسندات",
      icon: <BoltOutlinedIcon />, color: "#059669",
      items: [
        can("JOURNAL_ENTRY_CREATE") && { key: "je", nameKey: "accountsMenu.journalEntry", nameFallback: "قيد يومية", descFallback: "قيد محاسبي يدوي", icon: <MenuBookOutlinedIcon />, color: "#D97706", link: "/journal-entries/new?type=1" },
        can("JOURNAL_ENTRY_CREATE") && { key: "opening", nameKey: "accountsMenu.openingEntry", nameFallback: "قيد افتتاحي", descFallback: "أرصدة بداية المدة", icon: <PostAddIcon />, color: "#64748B", link: "/journal-entries/new?type=2" },
        can("BOND_RECEIPT_CREATE") && { key: "receipt", nameKey: "accountsMenu.generalReceipt", nameFallback: "سند قبض", descFallback: "قبض عام لحساب إيراد أو غيره", icon: <ArrowCircleDownOutlinedIcon />, color: "#059669", link: "/vouchers/general-receipt" },
        can("BOND_PAYMENT_CREATE") && { key: "payment", nameKey: "accountsMenu.generalPayment", nameFallback: "سند صرف", descFallback: "صرف عام لمصروف أو غيره", icon: <ArrowCircleUpOutlinedIcon />, color: "#E11D48", link: "/vouchers/general-payment" },
        can("JOURNAL_ENTRY_CREATE") && { key: "tax", nameKey: "accountsMenu.taxPayment", nameFallback: "سند صرف ضريبة", descFallback: "صرف مع ضريبة القيمة المضافة", icon: <CalculateIcon />, color: "#7C3AED", link: "/vouchers/tax-payment" },
      ],
    },
    {
      key: "reports",
      titleKey: "accountsMenu.sections.reports", titleFallback: "التقارير",
      subtitleKey: "accountsMenu.sections.reportsDesc", subtitleFallback: "القوائم المالية والكشوفات",
      icon: <InsightsOutlinedIcon />, color: "#0891B2",
      items: [
        can("VOUCHER_VIEW") && { key: "vouchers", nameKey: "accountsMenu.vouchersList", nameFallback: "قائمة السندات", icon: <ListAltIcon />, color: "#3B82F6", link: "/vouchers-list" },
        can("JOURNAL_ENTRY_VIEW") && { key: "journal", nameKey: "accountsMenu.journalEntries", nameFallback: "دفتر اليومية", icon: <MenuBookOutlinedIcon />, color: "#D97706", link: "/journal-entries" },
        can("ACCOUNT_STATEMENT_VIEW") && { key: "statement", nameKey: "accountsMenu.accountStatement", nameFallback: "كشف حساب", icon: <ReceiptLongOutlinedIcon />, color: "#DB2777", link: "/reports/account-statement" },
        can("TRIAL_BALANCE_VIEW") && { key: "trial", nameKey: "accountsMenu.trialBalance", nameFallback: "ميزان المراجعة", icon: <BalanceIcon />, color: "#7C3AED", link: "/reports/trial-balance" },
        can("INCOME_STATEMENT_VIEW") && { key: "income", nameKey: "accountsMenu.incomeStatement", nameFallback: "قائمة الدخل", icon: <QueryStatsIcon />, color: "#059669", link: "/reports/income-statement" },
        can("BALANCE_SHEET_VIEW") && { key: "bs", nameKey: "accountsMenu.balanceSheet", nameFallback: "الميزانية العمومية", icon: <InventoryOutlinedIcon />, color: "#0891B2", link: "/reports/balance-sheet" },
        can("REPORT_VIEW") && { key: "daily", nameKey: "accountsMenu.dailyMovement", nameFallback: "الحركة اليومية", icon: <TodayIcon />, color: "#EA580C", link: "/reports/daily-movement" },
        can("REPORT_VIEW") && { key: "vat", nameKey: "accountsMenu.vatReturn", nameFallback: "الإقرار الضريبي", icon: <PercentIcon />, color: "#0F766E", link: "/VatReportPage" },
        canProfit && { key: "profit", nameKey: "accountsMenu.salesProfitAnalysis", nameFallback: "تحليل أرباح المبيعات", descFallback: "أرباح الأصناف (من قائمة المبيعات)", icon: <SavingsOutlinedIcon />, color: "#16A34A", link: "/reports/items-profit-summary" },
      ],
    },
  ];

  return (
    <MenuPage
      titleKey="accountsMenu.title" titleFallback="المالية والحسابات"
      subtitleKey="accountsMenu.subtitle" subtitleFallback="دليل الحسابات والقيود والسندات والقوائم المالية"
      icon={<AccountBalanceOutlinedIcon />}
      sections={sections}
    />
  );
}